import React, { useState, useEffect, useRef } from 'react';
import { adminGetServices, adminAddService, adminDeleteService } from '../../api/admin';
import AdminLayout from './AdminLayout';
import './AdminServices.css';

// Font Awesome Icons
import { FaPlus, FaEdit, FaTrash, FaImage, FaToggleOn, FaToggleOff } from 'react-icons/fa';

const EMPTY_FORM = { id: null, title: '', short_desc: '', description: '', icon_class: '', sort_order: 0, is_active: 1 };

function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    adminGetServices().then(res => {
      if (res.success) setServices(res.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY_FORM); setImageFile(null); setImagePreview(''); setShowForm(true); setMsg(''); };
  const openEdit = (s) => { setForm({ ...s }); setImageFile(null); setImagePreview(s.image_url || ''); setShowForm(true); setMsg(''); };
  const closeForm = () => { setShowForm(false); setMsg(''); };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setMsg('Title is required'); return; }
    setSaving(true); setMsg('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k !== 'id' && v !== null) fd.append(k, v);
      });
      if (imageFile) fd.append('image', imageFile);

      let res;
      if (form.id) {
        fd.append('id', form.id);
        fd.append('_method', 'PUT');
        res = await adminAddService(fd);
      } else {
        res = await adminAddService(fd);
      }

      if (res.success) { setMsg('Saved successfully!'); load(); setTimeout(closeForm, 1000); }
      else setMsg('Error: ' + res.message);
    } catch { setMsg('Network error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await adminDeleteService(id);
    load();
  };

  const handleToggle = async (s) => {
    const fd = new FormData();
    Object.entries({ ...s, is_active: s.is_active ? 0 : 1 }).forEach(([k, v]) => {
      if (k !== 'id' && v !== null) fd.append(k, v);
    });
    fd.append('id', s.id);
    fd.append('_method', 'PUT');
    await adminAddService(fd);
    load();
  };

  return (
    <AdminLayout title="Services">

      <div className="adm-page-header">
        <h2>Manage Services</h2>
        <button className="adm-btn-primary" onClick={openAdd}>
          <FaPlus /> Add Service
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="adm-modal-overlay" onClick={closeForm}>
          <div className="adm-modal" onClick={e => e.stopPropagation()}>
            <div className="adm-modal__header">
              <h3>{form.id ? 'Edit Service' : 'Add New Service'}</h3>
              <button onClick={closeForm}>✕</button>
            </div>
            <div className="adm-modal__body">
              {msg && <div className={`adm-msg ${msg.startsWith('Saved') ? 'adm-msg--success' : 'adm-msg--error'}`}>{msg}</div>}

              <div className="adm-form-row">
                <div className="adm-form-field">
                  <label>Title *</label>
                  <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Service title" />
                </div>
                <div className="adm-form-field">
                  <label>Icon</label>
                  <input value={form.icon_class} onChange={e => setForm({...form, icon_class: e.target.value})} placeholder="icon-class" />
                </div>
              </div>

              <div className="adm-form-field">
                <label>Short Description</label>
                <textarea rows={2} value={form.short_desc} onChange={e => setForm({...form, short_desc: e.target.value})} placeholder="Brief description shown on cards" />
              </div>

              <div className="adm-form-field">
                <label>Full Description</label>
                <textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Full service details" />
              </div>

              <div className="adm-form-row">
                <div className="adm-form-field">
                  <label>Sort Order</label>
                  <input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: e.target.value})} />
                </div>
                <div className="adm-form-field">
                  <label>Status</label>
                  <select value={form.is_active} onChange={e => setForm({...form, is_active: parseInt(e.target.value)})}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="adm-form-field">
                <label>Service Image {form.id && imagePreview ? '(current image shown - upload to replace)' : ''}</label>
                <div className="adm-upload-box" onClick={() => fileRef.current.click()}>
                  {imagePreview
                    ? <img src={imagePreview} alt="preview" className="adm-upload-preview" />
                    : <div className="adm-upload-placeholder">
                        <FaImage /> Click to upload image
                      </div>
                  }
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleImage} />
              </div>
            </div>
            <div className="adm-modal__footer">
              <button className="adm-btn-secondary" onClick={closeForm}>Cancel</button>
              <button className="adm-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Service'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="adm-section">
        {loading ? <div className="adm-loading">Loading...</div> : (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Short Desc</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888' }}>No services added yet.</td></tr>
                )}
                {services.map(s => (
                  <tr key={s.id}>
                    <td>
                      {s.image_url
                        ? <img src={s.image_url} alt={s.title} className="adm-thumb" />
                        : <span className="adm-no-img">{s.icon_class || '📄'}</span>
                      }
                    </td>
                    <td><strong>{s.title}</strong></td>
                    <td className="adm-truncate">{s.short_desc}</td>
                    <td>{s.sort_order}</td>
                    <td>
                      <button className={`adm-toggle ${s.is_active ? 'adm-toggle--on' : 'adm-toggle--off'}`} onClick={() => handleToggle(s)}>
                        {s.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        {s.is_active ? ' Active' : ' Inactive'}
                      </button>
                    </td>
                    <td>
                      <div className="adm-actions">
                        <button className="adm-btn-edit" onClick={() => openEdit(s)}>
                          <FaEdit /> Edit
                        </button>
                        <button className="adm-btn-delete" onClick={() => handleDelete(s.id, s.title)}>
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminServices;